import { useEffect, useRef, useCallback } from 'react';

/**
 * Reliable auto-logout hook using polling instead of a single long setTimeout.
 * Browsers throttle/pause setTimeout in background tabs, so we check elapsed time
 * every 30 seconds against the last activity timestamp.
 */
export function useIdleTimeout(timeout = 20 * 60 * 1000, onTimeout, enabled = true, onKeepAlive = null, keepAliveInterval = 4 * 60 * 1000) {
  const lastActivityRef = useRef(Date.now());
  const lastKeepAliveRef = useRef(Date.now());
  const intervalRef = useRef(null);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    // Reset on mount
    lastActivityRef.current = Date.now();
    lastKeepAliveRef.current = Date.now();

    // Track user activity events
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => document.addEventListener(e, resetActivity, { passive: true }));

    // Poll every 30s to check if idle timeout has been exceeded
    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastActivityRef.current;
      if (elapsed >= timeout) {
        clearInterval(intervalRef.current);
        events.forEach(e => document.removeEventListener(e, resetActivity));
        onTimeout();
        return;
      }
      // Keep the server session alive while the user is actively using the app
      // (typing counts as activity but makes no API calls, so ping to slide the
      // server's idle window and avoid logging out someone mid-task).
      if (onKeepAlive &&
          (now - lastKeepAliveRef.current) >= keepAliveInterval &&
          lastActivityRef.current > lastKeepAliveRef.current) {
        lastKeepAliveRef.current = now;
        onKeepAlive();
      }
    }, 30000);

    // Also handle visibility change — check immediately when tab becomes visible
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= timeout) {
          clearInterval(intervalRef.current);
          events.forEach(e => document.removeEventListener(e, resetActivity));
          onTimeout();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      events.forEach(e => document.removeEventListener(e, resetActivity));
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [timeout, onTimeout, enabled, resetActivity, onKeepAlive, keepAliveInterval]);

  return { resetTimeout: resetActivity };
}

export default useIdleTimeout;
